// 세트 장비 창 표시 모델(순수 로직, 2026-10-27 대표님 지시 "그래픽 위주 세트 창"). DOM은 setPanel.ts가 그린다.
// 8세트(지역 6 + 레이드 2)마다 4부위 칸 상태(장착·보유·없음), 2·4세트 효과 발동 여부, 얻는 곳(지역 보스·레이드).
// 발동 판정은 실제 능력치 합산(sumGear)과 같은 activeSets를 쓴다(화면과 판정이 어긋나지 않게).
import { RAIDS, SETS } from '../shared/content/items';
import type { EquipSlot, SetDef } from '../shared/content/types';
import { activeSets, equipDef, type GearItem } from '../shared/gear';
import { SLOT_ORDER } from '../shared/gearLedger';
import type { GearView } from '../shared/gearView';
import { setPieceIconPath } from './itemIcons';
import { gearIconFallback } from './gearModel';

/** 부위 칸 상태: equipped 장착 중 · owned 가방에 있음 · missing 없음(실루엣) */
export type SetPieceState = 'equipped' | 'owned' | 'missing';

export interface SetPieceView {
  readonly slot: EquipSlot;
  readonly icon: string;
  /** 세트 그림이 아직 없을 때 쓰는 단계 그림(helm_t1 등) */
  readonly fallback: string;
  readonly state: SetPieceState;
  /** 장착 중이면 그 개체, 아니면 가방에서 가장 강한 개체(없으면 null) — 누르면 장비 탭에서 고른다 */
  readonly uid: string | null;
}

export interface SetBonusView {
  /** 필요 부위 수(2·4) */
  readonly need: 2 | 4;
  readonly text: string;
  readonly on: boolean;
}

export interface SetCardView {
  readonly id: string;
  readonly name: string;
  readonly pieces: readonly SetPieceView[];
  /** 장착 부위 수(0..4) */
  readonly equipped: number;
  /** 가진 부위 수(장착 + 가방, 부위 기준 0..4) */
  readonly owned: number;
  readonly bonuses: readonly [SetBonusView, SetBonusView];
  /** 얻는 곳 짧은 배지 글(예: "지역3 보스", "서리 거인") */
  readonly sources: readonly string[];
  /** 레이드 전용 세트 */
  readonly raid: boolean;
}

export interface SetPanelView {
  readonly cards: readonly SetCardView[];
  /** 지금 장착 중인 세트(1부위 이상) 요약 — 맨 위 그림 칸. 장착 부위 많은 순 */
  readonly worn: readonly { readonly id: string; readonly name: string; readonly icon: string; readonly count: number; readonly on2: boolean; readonly on4: boolean }[];
  /** 효과가 켜진(2세트 이상) 세트 수 */
  readonly activeCount: number;
}

/** 세트를 얻는 곳: 지역 세트는 그 지역 보스(중간·지역보스 드롭 표), 레이드 보상 목록(RaidDef.loot.sets)에 있으면 그 레이드도 */
export function setSources(def: SetDef): string[] {
  const out: string[] = [];
  if (def.region !== 'raid') out.push('지역' + def.region + ' 보스');
  for (const r of RAIDS) if (r.loot.sets.includes(def.id)) out.push(r.name);
  return out;
}

const GRADE_RANK: Readonly<Record<string, number>> = { common: 0, rare: 1, epic: 2, set: 3, legend: 4 };
/** 가방 정렬(gearModel.bagCards)과 같은 순서: 등급 → 아이템 레벨 → 강화 */
function stronger(a: GearItem, b: GearItem): boolean {
  const g = (GRADE_RANK[a.grade] ?? 0) - (GRADE_RANK[b.grade] ?? 0);
  if (g !== 0) return g > 0;
  if (a.ilvl !== b.ilvl) return a.ilvl > b.ilvl;
  return a.enh > b.enh;
}

/** 세트 창 모델. view.items·equipped만 읽는다(할당은 창을 다시 그릴 때만) */
export function setPanelModel(view: Pick<GearView, 'items' | 'equipped'>): SetPanelView {
  const eqUids = new Set<string>();
  for (const k of SLOT_ORDER) { const u = view.equipped[k]; if (u) eqUids.add(u); }
  const eqItems: (GearItem | null)[] = SLOT_ORDER.map((k) => { const u = view.equipped[k]; return u ? view.items.find((x) => x.uid === u) ?? null : null; });
  const act = activeSets(eqItems);
  const cards = SETS.map((def): SetCardView => {
    const pieces = def.pieces.map((slot): SetPieceView => {
      const defId = 'eq_' + def.id + '_' + slot;
      let worn: GearItem | null = null, best: GearItem | null = null;
      for (const it of view.items) {
        if (it.def !== defId || equipDef(it.def)?.setId !== def.id) continue;
        if (eqUids.has(it.uid)) { worn = it; break; }
        if (!best || stronger(it, best)) best = it;
      }
      const fallback = gearIconFallback({ uid: '', def: defId, grade: 'set', ilvl: 1, affixes: [], enh: 0, pity: [] });
      return { slot, icon: setPieceIconPath(def.id, slot), fallback, state: worn ? 'equipped' : best ? 'owned' : 'missing', uid: (worn ?? best)?.uid ?? null };
    });
    const a = act.find((x) => x.def.id === def.id);
    const equipped = a ? a.count : 0;
    return {
      id: def.id, name: def.name, pieces, equipped, owned: pieces.filter((p) => p.state !== 'missing').length,
      bonuses: [{ need: 2, text: def.bonus2, on: !!a?.on2 }, { need: 4, text: def.bonus4, on: !!a?.on4 }],
      sources: setSources(def), raid: def.region === 'raid',
    };
  });
  const worn = act
    .slice()
    .sort((x, y) => y.count - x.count || SETS.indexOf(x.def) - SETS.indexOf(y.def))
    .map((x) => ({ id: x.def.id, name: x.def.name, icon: setPieceIconPath(x.def.id, x.def.pieces[0]), count: x.count, on2: x.on2, on4: x.on4 }));
  return { cards, worn, activeCount: act.filter((x) => x.on2).length };
}
